import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../header/Header";
import Sidebarr from "../sidebar/Sidebar";
import VendorSidebar from "../sidebar/VendorSidebar";
import Tool from "../sidebar/Tool";
import Footer from "../footer/Footer";
import axios from "../helper/axios";
import moment from "moment";
import LoadingImg from "./loading.gif";
import { useSelector } from "react-redux";
import XLSX from "sheetjs-style";
import { saveAs } from "file-saver";
import "./Checkout.css"

function getOrderLocationBrand(order) {
  const formatPair = (location, brand) => {
    const loc = location ? String(location).trim() : "";
    const br = brand ? String(brand).trim() : "";

    if (loc && br) return `${loc} - ${br}`;
    if (loc) return loc;
    if (br) return br;
    return null;
  };

  const fromOrder = formatPair(order?.location, order?.brand);
  if (fromOrder) return fromOrder;

  const pairs = [
    ...new Set(
      (order?.productList || [])
        .map((item) =>
          formatPair(item?.location, item?.brand || item?.vendor)
        )
        .filter(Boolean)
    ),
  ];

  return pairs.length ? pairs.join(", ") : "-";
}

function formatOrderDate(createdAt) {
  if (!createdAt) return "-";
  return moment(createdAt).format("DD.MM.YYYY HH:mm");
}

function getShippingMethodLabel(shippingMethod) {
  return shippingMethod === "boxes" ? "BOXES" : "STANDARD";
}

function getOrderPrice(order) {
  return (order?.productList || []).reduce((sum, product) => {
    const qty = Number(product?.quantity) || 0;
    const total = Number(product?.totalPrice || 0);
    const grand = Number(product?.grandTotal || 0);
    const shipping = Number(product?.shipping_price || 0);
    let amount = grand > 0 || grand < 0 ? grand : total + shipping;
    if (qty < 0 && amount > 0) amount = -amount;
    if (qty > 0 && amount < 0) amount = Math.abs(amount);
    return sum + amount;
  }, 0);
}

function hasOrderFilters(filters) {
  return Boolean(
    filters?.statusValue ||
    filters?.dateFromValue ||
    filters?.dateToValue ||
    filters?.skuCode ||
    filters?.nameOrId ||
    filters?.productName ||
    filters?.locationValue
  );
}

function getOrderClientName(order) {
  return [order?.fname, order?.lname].filter(Boolean).join(" ").trim() || "-";
}

function buildOrderExcelRow(order) {
  return {
    "Order Id": order?.orderId || "",
    Client: getOrderClientName(order),
    Location: getOrderLocationBrand(order),
    Date: formatOrderDate(order?.createdAt),
    Price: getOrderPrice(order).toFixed(2),
    "Shipping Method": getShippingMethodLabel(order?.shippingMethod),
    "Payment Mode": order?.paymentMethod === "cod" ? "COD" : "CARD",
    Status: order?.status || "",
    Check: "",
  };
}

export default function ProductList() {
  const auth = useSelector((state) => state.auth);
  const { location: locationParam } = useParams();

  /* ---------------- FILTERS ---------------- */
  const [statusValue, setStatusValue] = useState("");
  const [dateFromValue, setDateFromValue] = useState("");
  const [dateToValue, setDateToValue] = useState("");
  const [skuCode, setSkuCode] = useState("");
  const [nameOrId, setNameOrId] = useState("");
  const [productName, setProductName] = useState("");
  const [locationValue, setLocationValue] = useState(locationParam || "");
  const [locationOptions, setLocationOptions] = useState([]);

  /* ---------------- DATA ---------------- */
  const [orders, setOrders] = useState([]);
  const [statusColor, setStatusColor] = useState([]);

  /* ---------------- PAGINATION ---------------- */
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const limit = 50;

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const observerRef = useRef(null);
  const fetchingRef = useRef(false);
  const filtersRef = useRef({});

  const activeLocationFilter = locationParam || locationValue;
  const filtersActive = hasOrderFilters({
    statusValue,
    dateFromValue,
    dateToValue,
    skuCode,
    nameOrId,
    productName,
    locationValue: activeLocationFilter,
  });

  filtersRef.current = {
    statusValue,
    dateFromValue,
    dateToValue,
    skuCode,
    nameOrId,
    productName,
    locationValue: activeLocationFilter,
  };

  /* ---------------- FETCH ORDERS ---------------- */
  const apiData = async (pageNo, replace = false) => {
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);

    const payload = {
      filterData: filtersRef.current,
      role: {
        productRole: auth?.user?.businessName,
        userRole: auth?.user?.role,
      },
      page: pageNo,
      limit,
    };

    try {
      const res = await axios.post("/getcheckout", payload);

      if (res.status === 200) {
        setOrders((prev) => (replace ? res.data.data : [...prev, ...res.data.data]));

        if (res.data.data.length < limit) {
          setHasMore(false);
        }

        setTotalOrders(res.data.pagination.totalCount);

        if (pageNo === 1) {
          setTotalPrice(
            filtersActive
              ? Number(res.data.pagination.totalPrice || 0)
              : 0
          );
        }
      }
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const res = await axios.get("/adminproduct/locations");
      if (res.status === 200) {
        setLocationOptions(res?.data?.locations || []);
      }
    } catch (error) {
      setLocationOptions([]);
    }
  };

  /* ---------------- STATUS COLORS ---------------- */
  const apiStatusColorData = async () => {
    const res = await axios.post("/getstatus");
    if (res.status === 200) {
      setStatusColor(res.data);
    }
  };

  useEffect(() => {
    apiStatusColorData();
    loadLocations();
  }, []);

  useEffect(() => {
    setLocationValue(locationParam || "");
    setOrders([]);
    setPage(1);
    setHasMore(true);
    setReloadKey((key) => key + 1);
  }, [locationParam]);

  /* ---------------- LOAD ON PAGE CHANGE ---------------- */
  useEffect(() => {
    apiData(page, page === 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, reloadKey]);

  /* ---------------- INFINITE SCROLL ---------------- */
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading]);

  /* ---------------- SEARCH ---------------- */
  const handleSearch = () => {
    setOrders([]);
    setPage(1);
    setHasMore(true);
    setReloadKey((key) => key + 1);
  };

  const fetchAllFilteredOrders = async () => {
    const exportLimit = 500;
    const allOrders = [];
    let pageNo = 1;

    while (true) {
      const res = await axios.post("/getcheckout", {
        filterData: filtersRef.current,
        role: {
          productRole: auth?.user?.businessName,
          userRole: auth?.user?.role,
        },
        page: pageNo,
        limit: exportLimit,
      });

      if (res.status !== 200) break;

      const batch = res.data?.data || [];
      if (!batch.length) break;

      allOrders.push(...batch);

      if (batch.length < exportLimit) break;
      pageNo += 1;
    }

    return allOrders;
  };

  const handleExportExcel = async () => {
    if (exporting) return;

    setExporting(true);

    try {
      const allOrders = await fetchAllFilteredOrders();

      if (!allOrders.length) {
        window.alert("No orders found to export.");
        return;
      }

      const excelData = allOrders.map(buildOrderExcelRow);
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      const dateLabel = moment().format("YYYY-MM-DD_HH-mm");
      saveAs(blob, `orders_${dateLabel}.xlsx`);
    } catch (error) {
      window.alert("Failed to export orders. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const deleteOrderData = async (id) => {
    const confirmDelete = window.confirm("Are you sure to delete?");
    if (!confirmDelete) return;

    await axios.post("/deleteorder", { _id: id });
    handleSearch();
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="sidebar-mini skin-green-light">
      <div className="wrapper">
        <Header />
        {auth?.user?.role === "Vendor" ? <VendorSidebar /> : <Sidebarr />}

        <div className="content-wrapper">
          <section className="content-header">
            <h1>
              Orders Management
              {activeLocationFilter ? (
                <small className="text-muted"> — Location: {activeLocationFilter}</small>
              ) : null}
            </h1>
          </section>

          <section className="content">
            {/* ---------------- SEARCH UI ---------------- */}


            <div className="box box-section">
  <div className="box-body search-section">
    <div className="search-grid">

      <select
        className="search-control"
        value={statusValue}
        onChange={(e) => setStatusValue(e.target.value)}
      >
        <option value="">Choose status</option>
        <option value="On hold">On Hold</option>
        <option value="Verifikuar">Verifikuar</option>
        <option value="Dërgesa gati">Dërgesa Gati</option>
        <option value="Realizuar">Realizuar</option>
        <option value="Nisur">Nisur</option>
        <option value="Pagesë e dështuar">Pagesë E Dështuar</option>
        <option value="Kthyer">Kthyer</option>
        <option value="Anuluar">Anuluar</option>
        <option value="Pagesë në pritje">Pagesë Në Pritje</option>
        <option value="Merre në dyqan">Merre Në Dyqan</option>
        <option value="Hapur">Hapur</option>
        <option value="Refuzuar">Refuzuar</option>
        <option value="Pjesërisht e kthyer">Pjesërisht E Kthyer</option>
      </select>

      <input
        type="date"
        className="search-control search-control-date"
        value={dateFromValue}
        onChange={(e) => setDateFromValue(e.target.value)}
        title="From Date"
      />

      <input
        type="date"
        className="search-control search-control-date"
        value={dateToValue}
        onChange={(e) => setDateToValue(e.target.value)}
        title="To Date"
      />

      <input
        type="text"
        className="search-control search-control-sm"
        placeholder="SKU Number"
        value={skuCode}
        onChange={(e) => setSkuCode(e.target.value)}
      />

      <input
        type="text"
        className="search-control search-control-sm"
        placeholder="Order ID"
        value={nameOrId}
        onChange={(e) => setNameOrId(e.target.value)}
      />

      <input
        type="text"
        className="search-control"
        placeholder="Customer Name"
        value={productName}
        onChange={(e) => setProductName(e.target.value)}
      />

      <select
        className="search-control"
        value={locationParam || locationValue}
        onChange={(e) => setLocationValue(e.target.value)}
        disabled={Boolean(locationParam)}
      >
        <option value="">All Locations</option>
        {locationOptions.map((location) => (
          <option key={location} value={location}>
            {location}
          </option>
        ))}
      </select>

      <button className="search-btn" onClick={handleSearch}>
        Search
      </button>

      <button
        className="export-excel-btn"
        onClick={handleExportExcel}
        disabled={exporting}
      >
        {exporting ? "Exporting..." : "Download Excel"}
      </button>

   <button className="total-order-btn">
        Total Orders: #{totalOrders}
        {filtersActive
          ? ` | Total Price: ${totalPrice.toFixed(2)} ${process.env.REACT_APP_CURRENCY}`
          : ""}
      </button>



    </div>
  </div>
</div>


            {/* ---------------- LOADER ---------------- */}
            {loading && (
              <div className="text-center my-3">
                <img src={LoadingImg} width="40" alt="" />
              </div>
            )}

            {/* ---------------- TABLE ---------------- */}
            <div className="box">
              <div className="box-body order-table">
                <div className="order-table-scroll">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th className="col-order-id">Order Id</th>
                      <th className="col-location">Location</th>
                      <th>Date</th>
                      <th>Price</th>
                      <th>Shipping Method</th>
                      <th>Payment Mode</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {orders.map((dt) => {
                      const totalPrice = getOrderPrice(dt);

                      return (
                        <tr key={dt._id}>
                          <td className="col-order-id">
                            <Link to={`/orderdetails/${dt._id}`} className="order-id-cell">
                              <span className="order-id-number">#{dt.orderId}</span>
                              <span className="order-id-name text-primary text-bold">
                                {dt.fname} {dt.lname}
                              </span>
                            </Link>
                          </td>

                          <td className="col-location">{getOrderLocationBrand(dt)}</td>

                          <td>
                            <span className="text-capitalize fs-1">
                              {formatOrderDate(dt?.createdAt)}
                            </span>
                          </td>



                          <td>
                            {totalPrice.toFixed(2)}{" "}
                            {process.env.REACT_APP_CURRENCY}
                          </td>

                          <td className="payment-status">
                            {getShippingMethodLabel(dt.shippingMethod)}
                          </td>

                          <td className="payment-status">
                            {dt.paymentMethod === "cod" ? "COD" : "CARD"}
                          </td>

                          <td>
                            {statusColor.map(
                              (s) =>
                                s.statusName === dt.status && (
                                  <span
                                    key={s._id}
                                    className="label label-default"
                                    style={{
                                      backgroundColor: s.statusColor,
                                      color: s.statusTextColor,
                                    }}
                                  >
                                    {dt.status}
                                  </span>
                                )
                            )}
                          </td>

                          <td>
                            <Link
                              to={`/orderdetails/${dt._id}`}
                              className="btn btn-default"
                            >
                              <i className="fa fa-eye" />
                            </Link>
                            {/* <button
                              onClick={() => deleteOrderData(dt._id)}
                              className="btn btn-default"
                            >
                              <i className="fa fa-trash" />
                            </button> */}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                {/* ---------------- SCROLL OBSERVER ---------------- */}
                <div ref={observerRef} />

                {!hasMore && (
                  <p className="text-center text-muted">No more orders</p>
                )}
              </div>
            </div>
          </section>
        </div>

        <Footer />
        <Tool />
      </div>
    </div>
  );
}
